import React, { useContext, useEffect, useState, useCallback } from 'react'
import { Topbar } from './topbar'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import { useHandleSignOut } from '../../hooks/useHandleSignOut'
import { AuthContext } from '../../context/AuthContext'
import { DashboardContext } from '../../context/DashboardContext'
import { OnboardingWelcomePage } from './OnboardingWelcomePage'
import { MonitorSmartphone } from 'lucide-react'

export function Dashboard() {
  const [isLoading, setIsLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const { refreshActiveWorkspace, activeWorkspace, setActiveWorkspace, eventsLoading, projectsLoading } =
    useContext(DashboardContext)
  const { products, meetings } = useContext(DashboardContext)
  const customFetch = useCustomFetch()
  const handleSignOut = useHandleSignOut()

  // State for tracking pending invitations to determine team completion
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [invitationsLoading, setInvitationsLoading] = useState(true)
  // Existing workspace members (accepted)
  const [workspaceMembers, setWorkspaceMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(true)

  // Function to fetch pending invitations
  const fetchInvitations = useCallback(async () => {
    if (!user?.active_workspace) return

    try {
      const response = await customFetch(`/getInvitesByWorkspace/${user.active_workspace}`, 'GET')
      setPendingInvitations(response.users || [])
    } catch (error) {
      console.error('Error fetching invitations:', error)
      setPendingInvitations([])
    } finally {
      setInvitationsLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Function to fetch accepted workspace members
  const fetchMembers = useCallback(async () => {
    if (!user?.active_workspace) return
    try {
      const response = await customFetch(`/getUsersByWorkspace/${user.active_workspace}`, 'GET')
      setWorkspaceMembers(response.users || [])
    } catch (error) {
      console.error('Error fetching workspace users:', error)
      setWorkspaceMembers([])
    } finally {
      setMembersLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Helper function to check if a specific onboarding step is completed
  const checkStepCompletion = useCallback(
    (stepId) => {
      // First check if the step is marked as completed in workspace data
      const completedSteps = activeWorkspace?.onboarding_completed_steps || []
      if (completedSteps.includes(stepId)) {
        return true
      }

      // Fallback to checking actual data (for backward compatibility)
      switch (stepId) {
        case 'product':
          return products && products.length > 0
        case 'recordings':
          return meetings && meetings.length > 0
        case 'integrations':
          // Check for integrations other than calendar (which is set up automatically)
          return (
            user?.integrations &&
            Object.entries(user.integrations).some(
              ([key, value]) =>
                Boolean(value) &&
                !key.toLowerCase().includes('calendar') &&
                !key.toLowerCase().includes('google') &&
                !key.toLowerCase().includes('microsoft')
            )
          )
        case 'team':
          // Team step completed when there's at least one other member (accepted or pending)
          if (invitationsLoading || membersLoading) {
            return false
          } else {
            // subtract current user from workspaceMembers
            const totalOthers = (pendingInvitations?.length || 0) + (workspaceMembers?.length || 0) - 1
            return totalOthers > 0
          }
        default:
          return false
      }
    },
    [
      activeWorkspace?.onboarding_completed_steps,
      products,
      meetings,
      user?.integrations,
      invitationsLoading,
      pendingInvitations,
      membersLoading,
      workspaceMembers,
    ]
  )

  // Check if all onboarding steps are completed (excluding skipped ones)
  const areAllOnboardingStepsCompleted = useCallback(() => {
    // Product step must be completed first
    if (!checkStepCompletion('product')) return false

    const optionalSteps = ['recordings', 'integrations', 'team']
    const skipped = activeWorkspace?.onboarding_skipped_steps || []

    return optionalSteps.every((step) => checkStepCompletion(step) || skipped.includes(step))
  }, [checkStepCompletion, activeWorkspace])

  // Helper function to determine if onboarding should be shown based on the workspace flag
  const shouldShowOnboarding = () => {
    // Avoid showing anything until the essential data has finished loading
    if (projectsLoading || eventsLoading || isLoading || invitationsLoading || membersLoading) return false

    // If the workspace document does not yet have the flag (legacy workspaces), assume onboarding was already completed
    if (activeWorkspace?.show_onboarding === undefined) return false

    return activeWorkspace.show_onboarding
  }

  // Determine if onboarding status has been evaluated (all relevant loading flags are false)
  const onboardingEvaluationReady = !(
    projectsLoading ||
    eventsLoading ||
    isLoading ||
    invitationsLoading ||
    membersLoading
  )

  const handleSubscribeToPlan = async (workspaceId, planId) => {
    try {
      const response = await customFetch('/createCheckoutUrl', 'POST', { workspaceId, planId })
      if (!response.hostedPageUrl) throw new Error('No plan URL found')
      window.location.href = response.hostedPageUrl
    } catch (error) {
      console.error('Error initiating payment', error)
    }
  }

  // Fetch invitations when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchInvitations()
    }
  }, [user?.active_workspace, fetchInvitations])

  // Fetch members when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchMembers()
    }
  }, [user?.active_workspace, fetchMembers])

  // When all steps are done (or skipped), flip the show_onboarding flag
  useEffect(() => {
    // Ensure we have the necessary data loaded
    if (activeWorkspace?.show_onboarding === false) return // already disabled
    if (projectsLoading || eventsLoading || isLoading || invitationsLoading || membersLoading) return

    if (areAllOnboardingStepsCompleted()) {
      ;(async () => {
        try {
          await customFetch('/updateWorkspace', 'PUT', {
            workspaceId: user.active_workspace,
            updates: { show_onboarding: false },
          })
          setActiveWorkspace({ ...activeWorkspace, show_onboarding: false })
        } catch (err) {
          console.error('Failed to update show_onboarding flag', err)
        }
      })()
    }
  }, [
    activeWorkspace?.show_onboarding,
    projectsLoading,
    eventsLoading,
    isLoading,
    invitationsLoading,
    membersLoading,
    areAllOnboardingStepsCompleted,
    customFetch,
    user.active_workspace,
    setActiveWorkspace,
    activeWorkspace,
  ])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    if (!activeWorkspace) return
    const paymentStatus = searchParams.get('payment_status')
    if (paymentStatus === 'success') return
    const planId = searchParams.get('plan_id')
    if (!planId) return
    const workspacesAmount = Object.keys(user.workspaces).length
    if (workspacesAmount !== 1) return
    if (activeWorkspace.plan_id) {
      navigate(`/workspace/select?plan_id=${planId}`)
    } else {
      handleSubscribeToPlan(Object.keys(user.workspaces)[0], planId)
    }
  }, [activeWorkspace])

  useEffect(() => {
    if (typeof user !== 'object' || Object.keys(user).length === 0) return
    // check if this is a redirect from the payment gateway
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success') {
      toast.success('Payment successful, might take a few minutes to reflect in your account')
      setTimeout(refreshActiveWorkspace, 30000)
      return
    }
    if (planId) {
      const workspacesAmount = Object.keys(user.workspaces).length
      if (workspacesAmount === 0) {
        navigate(`/workspace/create?plan_id=${planId}`)
      } else if (workspacesAmount > 1) {
        navigate(`/workspace/select?plan_id=${planId}`)
      }
    }
  }, [user.workspaces])

  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status')
    const planId = searchParams.get('plan_id')
    if (paymentStatus === 'success' || !planId) {
      setIsLoading(false)
    }
  }, [searchParams])

  // If onboarding is complete, show message to use desktop
  if (onboardingEvaluationReady && !shouldShowOnboarding()) {
    return (
      <div className="flex h-screen bg-canvas items-center justify-center p-6">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-8">
            <MonitorSmartphone className="h-24 w-24 text-gray-400 mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="text-2xl font-bold text-zinc mb-4">Evermuse Is Optimized for Desktop</h1>
            <p className="text-base text-slate leading-relaxed mb-6">
              To access the full Evermuse experience with all features, please switch to a desktop browser.
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full h-12 px-6 py-3 bg-pink rounded text-white text-base font-medium hover:bg-pink/90"
          >
            Sign Out
          </button>
        </div>
      </div>
    )
  }

  // Only render onboarding views
  return (
    <div className="flex h-screen bg-white dark:bg-gray-900">
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar for onboarding */}
        <div className="h-[50px] md:h-[70px] flex-shrink-0 border-b-2 border-grey-200 dark:border-gray-700 z-10">
          <Topbar isOnboarding={true} />
        </div>

        {/* Main content area - only show onboarding */}
        <div className="flex-1 hideScrollbar z-0 h-[calc(100vh-50px)]" style={{ paddingLeft: '0px' }}>
          <div className="h-full">
            {onboardingEvaluationReady && shouldShowOnboarding() && <OnboardingWelcomePage />}
          </div>
        </div>
      </div>
    </div>
  )
}
